import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import { getAffiliateName, getAssignedManagers } from "utils/clientHelper";
import { useForm } from "react-hook-form";
import { FIELD_REQUIRED } from "constants/popup-codes";
import "./ClientForms.scss";
import InputText from "core/inputs/input-text/InputText";
import { Controller } from "utils/Controller";
import {
  FILTERS_FOR_NAMES,
  INPUTS_IDS,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useRef } from "react";
import {
  renderInputAddress,
  renderInputAffiliate,
  renderInputCity,
  renderInputCountry,
  renderInputPicture,
  renderInputPostalCode,
  renderInputAuthorizedManagers,
  renderInputSector,
} from "../form/CommonInputs";
import {
  CLIENT_FORM_LABELS,
  CLIENT_FORM_PLACEHOLDERS,
  CLIENT_FORM_DEFAULT_VALUES,
} from "constants/forms";
import { PropTypes } from "prop-types";
import ToggleButton from "core/buttons/toggle-button/ToggleButton";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useUserContext } from "api/user/UserProvider";
import colors from "assets/styles/mixins/colors.scss";

const ClientGeneralInfo = ({
  readOnly,
  setReadOnly,
  onSubmit,
  getStatus,
  getCode,
}) => {
  const {
    authorizedManager,
    clientBySlug,
    clientsList,
    getClients,
    dataCountry,
    id,
    lang,
    sectorList,
    cellHighlight,
    setIsValidated,
    setIsStrategicClient,
    isStrategicClient,
    picture,
    createFile,
    createdFile,
    setCreatedFile,
    editClientStatus,
    isValidationModalOpen,
    apiErrorResponse,
  } = useContext(InfoContext);

  const { getNamesByFilters, namesByFilters } = useGeneralContext();

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: CLIENT_FORM_DEFAULT_VALUES,
  });
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  const { affiliate_id, assigned_managers } = clientBySlug || {};

  const affiliateName = getAffiliateName({
    list: namesByFilters,
    id: affiliate_id,
  });

  const assignedManagers = getAssignedManagers({
    list: namesByFilters,
    ids: assigned_managers,
  });

  useEffect(() => {
    const ids = [affiliate_id, ...(assigned_managers || [])].filter(Boolean);

    if (ids?.length > 0) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.CLIENT, FILTERS_FOR_NAMES.PERSON],
        id: ids,
      });
    }
  }, [clientBySlug]);

  const initForm = () => {
    const defaultKeys = Object.keys(CLIENT_FORM_DEFAULT_VALUES);
    Object.keys(clientBySlug).forEach((elm) => {
      if (defaultKeys.includes(elm) && clientBySlug[elm]) {
        if (elm === "affiliate_id") {
          setValue("affiliate_id", affiliateName);
        } else if (elm === "assigned_managers") {
          setValue("assigned_managers", assignedManagers);
        } else {
          setValue(elm, clientBySlug[elm]);
        }
      }
    });
    setIsStrategicClient(Boolean(clientBySlug.strategic));
  };

  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
  };

  useEffect(() => {
    setValue("picture_url", createdFile?.uri);
  }, [createdFile]);

  useEffect(() => {
    if (
      readOnly &&
      !_.isEmpty(clientBySlug) &&
      !isValidationModalOpen &&
      editClientStatus !== QUERY_STATUS.ERROR &&
      editClientStatus !== QUERY_STATUS.LOADING
    ) {
      initForm();
    }
  }, [
    clientBySlug,
    isValidationModalOpen,
    editClientStatus,
    readOnly,
    namesByFilters,
  ]);

  const { setApiErrorResponse } = useUserContext();

  const onReset = () => {
    initForm();
    setReadOnly(true);
    clearErrors();
    setApiErrorResponse(null);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const renderInputClientName = () => (
    <Controller
      {...{
        control,
        register,
        name: "name",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputText
            id="name"
            errors={errors["name"]}
            readOnly={readOnly}
            label={CLIENT_FORM_LABELS.name}
            placeholder={CLIENT_FORM_PLACEHOLDERS.name}
            required
            {...props}
          />
        ),
      }}
    />
  );

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit(onGuardedSubmit, onError)}>
      <div className="section-1">
        <div
          className="strategic-client-toggle"
          style={{ marginBottom: "32px" }}
        >
          <ToggleButton
            id="strategic-client"
            label={
              <FormattedMessage
                id="strategic-client"
                defaultMessage="Client stratégique"
              />
            }
            readOnly={readOnly}
            onChange={(val) => setIsStrategicClient(val)}
            value={isStrategicClient}
          />
        </div>
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id="who_s_she_he"
              defaultMessage="WHO'S SHE/HE ?"
            />
          }
        />

        <div className="client-personal-info-form">
          <div className="left-block">
            {!id && (
              <div className="corner-left">
                {renderInputPicture({
                  label: { id: "input_logo_label" },
                  readOnly,
                  id: INPUTS_IDS.CLIENT,
                  value: picture,
                  createFile,
                  setCreatedFile,
                  control,
                  register,
                  errors,
                })}
              </div>
            )}
            <div className="center-block">
              <div
                className={`cell-padding ${
                  !watch("name") && cellHighlight && "errorCellColor"
                }`}
                style={{ borderRadius: id ? "6px 0 0 0" : "0" }}
              >
                {renderInputClientName()}
              </div>
              <div
                className={`cell-padding ${
                  !watch("sector_id") && cellHighlight && "errorCellColor"
                }`}
              >
                {renderInputSector({
                  control,
                  errors,
                  label: CLIENT_FORM_LABELS.sector,
                  placeholder: CLIENT_FORM_PLACEHOLDERS.sector,
                  readOnly,
                  register,
                  setValue,
                  value: watch("sector_id")?.[0]?.value || "",
                  sectorList,
                  lang,
                })}
              </div>
              <div
                className={`cell-padding ${
                  !watch("country") && cellHighlight && "errorCellColor"
                }`}
                style={{ borderRadius: id ? "0 0 0 6px" : "unset" }}
              >
                {renderInputCountry({
                  control,
                  errors,
                  label: CLIENT_FORM_LABELS.country,
                  placeholder: CLIENT_FORM_PLACEHOLDERS.country,
                  readOnly,
                  register,
                  countries: dataCountry,
                  value: watch("country"),
                  setValue,
                  lang,
                })}
              </div>
              <div
                className={`cell-padding ${
                  !watch("city") && cellHighlight && "errorCellColor"
                }`}
              >
                {renderInputCity({
                  control,
                  errors,
                  label: CLIENT_FORM_LABELS.city,
                  placeholder: CLIENT_FORM_PLACEHOLDERS.city,
                  readOnly,
                  register,
                })}
              </div>
              <div
                className={`cell-padding ${
                  !watch("postal_code") && cellHighlight && "errorCellColor"
                }`}
              >
                {renderInputPostalCode({
                  control,
                  errors,
                  label: CLIENT_FORM_LABELS.postal_code,
                  placeholder: CLIENT_FORM_PLACEHOLDERS.postal_code,
                  readOnly,
                  register,
                })}
              </div>
            </div>
            <div
              className={`corner-right ${
                !watch("address") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderInputAddress({
                control,
                errors,
                label: CLIENT_FORM_LABELS.address,
                placeholder: CLIENT_FORM_PLACEHOLDERS.address,
                readOnly,
                register,
                id: CLIENT_FORM_LABELS.address.id,
              })}
            </div>
          </div>
          <div className="right-block">
            <Typography
              className="title-3 violet text-uppercase"
              type="h2"
              label={
                <FormattedMessage
                  id="assignment"
                  defaultMessage="Affectation"
                />
              }
            />
            <div className="line">
              <div
                className={`cell-padding ${
                  errors?.["affiliate_id"] || apiErrorResponse?.["affiliate_id"]
                    ? "errorCellColor"
                    : ""
                }`}
              >
                {renderInputAffiliate({
                  control,
                  errors:
                    errors["affiliate_id"] ||
                    apiErrorResponse?.["affiliate_id"],
                  errorMessage:
                    apiErrorResponse?.["affiliate_id"] ||
                    errors?.["affiliate_id"]?.message,
                  getOptions: (search) => getClients({ search }),
                  label: CLIENT_FORM_LABELS.affiliate_id,
                  clientsList: clientsList?.map((elm) => ({
                    label: elm?.name,
                    value: elm?.id,
                  })),
                  readOnly,
                  required: false,
                  register,
                  setValue,
                  value: readOnly ? affiliateName : watch("affiliate_id"),
                })}
              </div>
              <div
                className={`cell-padding ${
                  errors?.["assigned_managers"] ||
                  apiErrorResponse?.["assigned_managers"]
                    ? "errorCellColor"
                    : ""
                }`}
              >
                {renderInputAuthorizedManagers({
                  control,
                  errors:
                    errors["assigned_managers"] ||
                    apiErrorResponse?.["assigned_managers"],
                  errorMessage:
                    apiErrorResponse?.["assigned_managers"] ||
                    errors?.["assigned_managers"]?.message,
                  getOptions: (search) => {
                    getNamesByFilters({
                      types: [FILTERS_FOR_NAMES.PERSON],
                      search,
                    });
                    return (
                      namesByFilters
                        ?.filter(
                          (person) => person.type === FILTERS_FOR_NAMES.PERSON
                        )
                        .map((person) => ({
                          label: person.name,
                          value: person.id,
                        })) || []
                    );
                  },
                  label: CLIENT_FORM_LABELS.assigned_managers,
                  personsList: authorizedManager,
                  readOnly,
                  register,
                  setValue,
                  value: readOnly
                    ? assignedManagers
                    : watch("assigned_managers"),
                  watch,
                  getNamesByFilters,
                  namesByFilters,
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!readOnly && (
        <div style={{ marginTop: "50px" }} className="form-buttons">
          <Button
            className="btn-standard btn-outline"
            type="button"
            onClick={onReset}
            id="button-form-reset"
          >
            <FormattedMessage id="cancel" defaultMessage="Annuler" />
          </Button>

          <Button
            color="btn-medium gray-700"
            type="submit"
            id="button-form-validation"
            onClick={() => setIsValidated(true)}
            backgroundColor={
              editClientStatus === QUERY_STATUS.LOADING && colors.disabledGrey
            }
            disabled={editClientStatus === QUERY_STATUS.LOADING}
          >
            <FormattedMessage
              id="button_form_validation_edition"
              defaultMessage="Valider l'édition"
            />
          </Button>
        </div>
      )}
    </form>
  );
};

export default ClientGeneralInfo;

ClientGeneralInfo.propTypes = {
  getCode: PropTypes.func,
  getStatus: PropTypes.func,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.bool,
  setReadOnly: PropTypes.func,
};
